import {
  CLASS_COLORS,
  formatDateTime,
  formatRelativeTime,
  itemLevelColor,
} from '@moussenaires/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { rosterApi } from '../api/roster.js';
import { CharacterMythicPlusPanel } from './CharacterMythicPlusPanel.js';
import { CharacterRaidPanel } from './CharacterRaidPanel.js';
import { FactionIcon } from './ClassBadge.js';
import { Modal } from './Modal.js';
import { SegmentedControl } from './SegmentedControl.js';
import './CharacterDetailModal.css';

interface CharacterDetailModalProps {
  characterId: number;
  isAdmin: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}

// Mythic+ score is deliberately not here — it lives only in the mythic+
// panel now, alongside the ranks, rather than in this tab-agnostic row.
const STAT_ROWS: {
  key: 'bestKeyLevel' | 'progressPercent' | 'achievementPoints' | 'pvpRating';
  labelKey: string;
}[] = [
  { key: 'bestKeyLevel', labelKey: 'roster.bestKeyLevel' },
  { key: 'progressPercent', labelKey: 'roster.progress' },
  { key: 'achievementPoints', labelKey: 'roster.achievementPoints' },
  { key: 'pvpRating', labelKey: 'roster.pvpRating' },
];

type DetailSection = 'raid' | 'mythicPlus';

export function CharacterDetailModal({
  characterId,
  isAdmin,
  onClose,
  onEdit,
  onDeleted,
}: CharacterDetailModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as 'fr' | 'en';
  const queryClient = useQueryClient();
  const [logTitle, setLogTitle] = useState('');
  const [logUrl, setLogUrl] = useState('');
  const [section, setSection] = useState<DetailSection>('raid');

  const query = useQuery({
    queryKey: ['roster', 'character', characterId],
    queryFn: () => rosterApi.character(characterId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => rosterApi.deleteCharacter(characterId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      onDeleted();
    },
  });

  const addLogMutation = useMutation({
    mutationFn: () => rosterApi.addLog(characterId, { url: logUrl.trim(), title: logTitle.trim() }),
    onSuccess: () => {
      setLogTitle('');
      setLogUrl('');
      void queryClient.invalidateQueries({ queryKey: ['roster', 'character', characterId] });
    },
  });

  const deleteLogMutation = useMutation({
    mutationFn: (logId: number) => rosterApi.deleteLog(logId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['roster', 'character', characterId] }),
  });

  const syncMutation = useMutation({
    mutationFn: () => rosterApi.syncCharacter(characterId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster', 'character', characterId] });
      void queryClient.invalidateQueries({ queryKey: ['roster', 'players'] });
    },
  });

  const character = query.data;
  const classColor = character?.class ? CLASS_COLORS[character.class] : 'var(--text-muted)';

  const title = character ? (
    <div className="character-detail-header">
      <FactionIcon faction={character.faction} />
      <span className="character-detail-name" style={{ color: classColor }}>
        {character.name}
      </span>
      {!character.class && (
        <span className="character-detail-syncing">{t('roster.notSyncedYet')}</span>
      )}
      <span className="character-detail-realm">
        {character.realm} · {t('roster.level')} {character.level}
        {character.stats.itemLevel != null && (
          <>
            {' · '}
            <span style={{ color: itemLevelColor(character.stats.itemLevel) }}>
              {Math.round(character.stats.itemLevel)} {t('roster.itemLevel')}
            </span>
          </>
        )}
      </span>
      {isAdmin && (
        <div className="character-detail-actions">
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            aria-label={t('roster.sync')}
            title={t('roster.sync')}
          >
            <RefreshCw
              size={15}
              className={syncMutation.isPending ? 'character-detail-sync-spinning' : ''}
            />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={onEdit}
            aria-label={t('common.edit')}
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            onClick={() => {
              if (window.confirm(t('roster.deleteCharacterConfirm', { name: character.name })))
                deleteMutation.mutate();
            }}
            aria-label={t('common.delete')}
          >
            <Trash2 size={15} />
          </button>
        </div>
      )}
    </div>
  ) : (
    '…'
  );

  return (
    <Modal title={title} onClose={onClose} width="xl">
      {!character ? (
        <div className="center-state">
          <div className="spinner" />
        </div>
      ) : (
        <div className="character-detail">
          {STAT_ROWS.filter((row) => character.stats[row.key] !== null).length > 0 && (
            <div className="character-detail-stats">
              {STAT_ROWS.filter((row) => character.stats[row.key] !== null).map((row) => (
                <div key={row.key} className="character-detail-stat">
                  <strong>{Math.round(character.stats[row.key]! * 10) / 10}</strong>
                  <span>{t(row.labelKey)}</span>
                </div>
              ))}
            </div>
          )}

          {character.notes && <p className="character-detail-notes">{character.notes}</p>}
          {character.profileUrl && (
            <a
              href={character.profileUrl}
              target="_blank"
              rel="noreferrer"
              className="character-detail-profile-link"
            >
              <ExternalLink size={13} /> {character.profileUrl}
            </a>
          )}

          {character.enrichment && (
            <p className="character-enrichment-fetched">
              {t('roster.lastSynced', {
                time: formatRelativeTime(character.enrichment.fetchedAt, locale),
              })}
            </p>
          )}

          <SegmentedControl
            className="character-detail-section-toggle"
            options={[
              { value: 'raid', label: t('roster.sortByRaid') },
              { value: 'mythicPlus', label: t('roster.sortByMythicPlus') },
            ]}
            value={section}
            onChange={setSection}
            ariaLabel={t('roster.viewSection')}
          />

          {section === 'raid' ? (
            <CharacterRaidPanel
              raidParses={character.enrichment?.raidParses ?? null}
              raidParsesError={character.enrichment?.errors.raidParses}
              recentLogs={character.enrichment?.recentLogs ?? null}
              recentLogsError={character.enrichment?.errors.recentLogs}
            />
          ) : (
            <CharacterMythicPlusPanel
              mythicPlus={character.enrichment?.mythicPlus ?? null}
              mythicPlusError={character.enrichment?.errors.raiderIo}
            />
          )}

          <h3 className="character-detail-section-title">{t('roster.highlightedLogs')}</h3>
          {character.logs.length === 0 ? (
            <p className="character-detail-empty">{t('roster.noLogs')}</p>
          ) : (
            <ul className="character-detail-logs">
              {character.logs.map((log) => (
                <li key={log.id}>
                  <a href={log.url} target="_blank" rel="noreferrer">
                    {log.title}
                  </a>
                  <span className="character-detail-log-date">
                    {formatDateTime(log.createdAt, locale)}
                  </span>
                  {isAdmin && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon"
                      onClick={() => deleteLogMutation.mutate(log.id)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {isAdmin && (
            <div className="character-detail-add-log">
              <input
                placeholder={t('roster.name')}
                value={logTitle}
                onChange={(event) => setLogTitle(event.target.value)}
              />
              <input
                placeholder="https://…"
                value={logUrl}
                onChange={(event) => setLogUrl(event.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                disabled={!logTitle.trim() || !logUrl.trim() || addLogMutation.isPending}
                onClick={() => addLogMutation.mutate()}
                aria-label={t('roster.addLog')}
              >
                <Plus size={15} />
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
